import { useEffect, useRef } from "react";
import { ThemeBadge } from "@/components/yarn/Badges";
import { useCountdown } from "@/hooks/useRoomSocket";
import { sound } from "@/lib/sound";

const LABELS = {
  waiting: ["Gathering the circle", "Need at least 3 players to start."],
  starting: ["Spinning a starter", "The AI is writing your opening line."],
  writing: ["Write the next line", "Keep it short. Keep it going."],
  voting: ["Vote for the best line", "You can't vote for your own."],
  results: ["Woven in!", "Next round starts in a moment."],
  ended: ["Story tied off", "Start a new one whenever you're ready."],
};

function CountdownRing({ left, total }) {
  const r = 26;
  const c = 2 * Math.PI * r;
  const pct = total ? Math.min(1, left / total) : 0;
  const urgent = left <= 10;
  return (
    <div className="relative w-16 h-16 shrink-0" data-testid="countdown-timer">
      <svg viewBox="0 0 64 64" width="64" height="64" className="-rotate-90">
        <circle cx="32" cy="32" r={r} fill="none" stroke="var(--yarn-stitch)" strokeWidth="6" strokeDasharray="4 3" />
        <circle cx="32" cy="32" r={r} fill="none" stroke={urgent ? "#DC2626" : "var(--yarn-terracotta)"} strokeWidth="6"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct)} style={{ transition: "stroke-dashoffset 0.25s linear" }} />
      </svg>
      <span className={`absolute inset-0 grid place-items-center font-display text-xl font-semibold ${urgent ? "text-red-600" : ""}`} data-testid="countdown-seconds">
        {Math.ceil(left)}
      </span>
    </div>
  );
}

export function PhaseBanner({ room, offset }) {
  const left = useCountdown(room.deadline, offset);
  const secs = Math.ceil(left);
  const lastTick = useRef(null);
  useEffect(() => {
    if (!room.deadline || !["writing", "voting"].includes(room.phase) || secs < 1 || secs > 10) return;
    const key = `${room.round}-${room.phase}-${secs}`;
    if (lastTick.current !== key) {
      lastTick.current = key;
      sound.tick();
    }
  }, [secs, room.deadline, room.phase, room.round]);
  const [title, sub] = LABELS[room.phase] || LABELS.waiting;
  const total = room.phase === "writing" ? room.round_seconds : room.phase === "voting" ? room.vote_seconds : 8;
  return (
    <div className="flex items-center justify-between gap-4" data-testid="phase-banner">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {room.round > 0 && <span className="font-display text-xs uppercase tracking-widest text-[var(--yarn-terracotta)]" data-testid="round-number">Round {room.round}</span>}
          <ThemeBadge theme={room.theme} testid="room-theme-badge" />
        </div>
        <h2 className="font-display text-2xl sm:text-3xl font-semibold mt-1" data-testid="phase-title">{title}</h2>
        <p className="text-sm text-[var(--yarn-muted)]">{sub}</p>
      </div>
      {room.deadline && <CountdownRing left={left} total={total} />}
    </div>
  );
}
